/**
 * Authentication API operations using the standardized RFC-001 envelopes.
 */
import { post, request, toProblem, unwrapData } from './base';
import type { ApiResponse, Problem } from './types';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isEmailVerified?: boolean;
  phoneNumber?: string;
  jobTitle?: string;
  role?: string;
  roles?: string[];
  capabilities?: string[];
  company?: {
    id: string;
    name: string;
  } | null;
  /** Never had a company membership — route to /complete-signup. */
  needsCompanySetup?: boolean;
  /** Had memberships that were revoked — route to /no-active-company. */
  hasPriorMembership?: boolean;
  /** One-shot flag: a Google account was just linked to this user. */
  googleConnectedToast?: boolean;
}

export interface AuthConfig {
  google_enabled: boolean;
}

export interface AuthError extends Error {
  code?: string;
  email?: string;
}

export interface LoginResult {
  user: AuthUser;
  meta?: ApiResponse<unknown>['meta'];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  password: string;
}

export interface VerifyEmailData {
  key: string;
}

export interface PasswordResetRequestData {
  email: string;
}

export interface PasswordResetConfirmData {
  uid: string;
  key: string;
  password1: string;
  password2: string;
}

export interface ResendVerificationEmailData {
  email: string;
}

export interface MessageResult {
  message: string;
  meta?: ApiResponse<unknown>['meta'];
}

const normalizeProblemCode = (code?: string | null) => (code ?? '').toString().toUpperCase();

const createAuthError = (problem: Problem, email?: string): AuthError => {
  const authError = new Error(
    problem.detail || problem.title || 'Authentication error'
  ) as AuthError;
  authError.code = normalizeProblemCode(problem.code);
  if (email) {
    authError.email = email;
  }
  return authError;
};

const extractMessageResult = (
  envelope: ApiResponse<{ message?: string }>,
  fallbackMessage: string
): MessageResult => {
  const data = unwrapData(envelope);
  const message = typeof data?.message === 'string' ? data.message : fallbackMessage;
  return { message, meta: envelope.meta };
};

/**
 * Get current authenticated user.
 */
export const getCurrentUser = async (): Promise<AuthUser | null> => {
  try {
    const response = await request<{ user: AuthUser | null }>('/auth/current-user/');
    const data = unwrapData(response);
    return data?.user ?? null;
  } catch (error) {
    const problem = toProblem(error);
    const code = normalizeProblemCode(problem.code);

    if (problem.status === 401 || code === 'NOT_AUTHENTICATED') {
      return null;
    }

    throw problem;
  }
};

/**
 * Login user with credentials.
 */
export const loginUser = async (credentials: LoginCredentials): Promise<LoginResult> => {
  try {
    const response = await request<{ user: AuthUser }>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    const data = unwrapData(response);
    return {
      user: data.user,
      meta: response.meta,
    };
  } catch (error) {
    const problem = toProblem(error);
    const code = normalizeProblemCode(problem.code);

    if (code === 'EMAIL_NOT_VERIFIED' || code === 'NO_ACTIVE_COMPANY') {
      throw createAuthError(problem, credentials.email);
    }

    throw problem;
  }
};

/**
 * Sign up new user.
 */
export const signupUser = async (userData: SignupData): Promise<MessageResult> => {
  try {
    const response = await request<{ message?: string }>('/auth/signup/', {
      method: 'POST',
      body: JSON.stringify({
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        company_name: userData.companyName,
        password1: userData.password,
        password2: userData.password,
      }),
    });

    return extractMessageResult(
      response,
      'Account registration processed. If this is a new email, you will receive a verification email shortly.'
    );
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Which optional sign-in methods are enabled in this environment.
 */
export const getAuthConfig = async (): Promise<AuthConfig> => {
  const response = await request<AuthConfig>('/auth/auth-config/');
  return unwrapData(response);
};

/**
 * Post-social-signup onboarding: name the company and provision it.
 */
export const completeSignup = async (companyName: string): Promise<AuthUser> => {
  try {
    const response = await request<{ user: AuthUser }>('/auth/complete-signup/', {
      method: 'POST',
      body: JSON.stringify({ company_name: companyName }),
    });
    return unwrapData(response).user;
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Logout current user.
 */
export const logoutUser = async (): Promise<void> => {
  try {
    await post<{ message?: string }>('/auth/logout/');
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Verify email with verification key.
 */
export const verifyEmail = async (data: VerifyEmailData): Promise<MessageResult> => {
  try {
    const response = await request<{ message?: string }>('/auth/verify-email/', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return extractMessageResult(response, 'Email verified successfully.');
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Check email verification status.
 */
export const checkEmailVerificationStatus = async (): Promise<{ isVerified: boolean }> => {
  const response = await request<{ isVerified: boolean }>('/auth/email-verification-status/');
  return unwrapData(response);
};

/**
 * Resend email verification.
 */
export const resendEmailVerification = async (
  data: ResendVerificationEmailData
): Promise<MessageResult> => {
  try {
    const response = await request<{ message?: string }>('/auth/resend-email-verification/', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return extractMessageResult(response, 'Verification email has been sent.');
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Request password reset.
 */
export const requestPasswordReset = async (
  data: PasswordResetRequestData
): Promise<MessageResult> => {
  try {
    const response = await request<{ message?: string }>('/auth/password-reset/', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return extractMessageResult(response, 'Password reset e-mail has been sent.');
  } catch (error) {
    throw toProblem(error);
  }
};

/**
 * Confirm password reset with new password.
 */
export const confirmPasswordReset = async (
  data: PasswordResetConfirmData
): Promise<MessageResult> => {
  try {
    const response = await request<{ message?: string }>('/auth/password-reset-confirm/', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return extractMessageResult(response, 'Password has been reset successfully.');
  } catch (error) {
    throw toProblem(error);
  }
};
